import { useDispatch } from "react-redux";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { requestFullProductData } from "~/domains/product/redux/actions";
import { Card } from "~/features/configurator/Linear/components/Card";
import { Controller, useFormContext } from "react-hook-form";
import { ActionIconButton } from "~/common/components/Button/ActionIconButton";
import {
  type ConfiguredQuickEntryItem,
  type QuickEntryForm,
  type QuickEntryItem,
  type QuickEntryItemPC,
} from "../types/models";
import { useOnMount } from "~/common/hooks/useOnMount";
import { type SrpProductData } from "~/utils/models";
import { fakeDescriptorFromProduct, isProductPMA } from "~/domains/product/utils/productDescriptorUtils";
import { NewVersionMessage } from "~/common/components/Messages/NewVersionMessage";
import { type ProductData } from "~/domains/product/types/ProductData";
import { UndoButton } from "~/common/components/Button/UndoButton";
import useMediaQuery from "~/common/hooks/useMediaQuery";
import { MEDIA_QUERY_MIN_TABLET } from "~/common/utils/screenSizes";
import {
  AutosuggestProductInput,
  isSameProduct,
  useFetchProductAutosuggest,
} from "~/common/components/AutosuggestProductInput/AutosuggestProductInput";
import { hasProductConfigured } from "~/features/quickEntry/utils/helpers";
import CustomNumberFormat from "~/common/components/CustomNumberFormat";
import Parameter from "~/common/components/Parameter";
import { useTranslation } from "react-i18next";
import { InfoProductMessagePMA } from "~/features/productDetails/components/ProductSummary/InfoProductMessagePMA";
import { Alert } from "~/common/mui/Alert/Alert";
import { Box, styled } from "~/common/mui";
import DeleteIcon from "@mui/icons-material/Delete";

interface RowProps extends DivAttributes {
  index: number;
  validateRow?: (row: ConfiguredQuickEntryItem, itemsPopup?: QuickEntryItemPC[]) => ReactNode[];
  item: QuickEntryItem;
  items?: QuickEntryItemPC[];
  onRemove: () => void;

  variant: "pc" | "wishlist" | "b2c-cart";
  rightSideColumns?: readonly string[];
}

export const QuickEntryTableRow = ({
  index,
  item,
  onRemove,
  validateRow,
  variant,
  items,
  rightSideColumns,
  ...rest
}: RowProps) => {
  const { t } = useTranslation();
  const {
    control,
    setValue,
    register,
    formState: { errors },
    setError,
    clearErrors,
  } = useFormContext<QuickEntryForm>();
  const [isUpdatedWithSuccessor, setUpdatedWithSuccessor] = useState(false);
  const [successorProduct, setSuccessorProduct] = useState<ProductData | null>(null);

  const formNamePrefix = `items.${index}` as const;

  function getFieldName<T extends keyof QuickEntryItem>(name: T) {
    return `items.${index}.${name}` as const;
  }

  const getError = (name: keyof QuickEntryItem) => errors.items?.[index]?.[name] ?? undefined;

  const fromSuccessorClick = useRef(false);

  useOnMount(() => {
    register(getFieldName("product"));
    register(getFieldName("productId"));
    register(getFieldName("previousProductId"));
  });

  const { product, productId, previousProductId } = item;

  const productCode = product?.code;
  const dispatch = useDispatch();
  useEffect(() => {
    if (productCode) {
      dispatch(requestFullProductData(productCode, ""));
    }
  }, [productCode]);

  const qtyError = getError("quantity");
  const productError = getError("product");

  const { inputProps, products, selectedProduct } = useFetchProductAutosuggest(productId ?? "");

  const refreshProductError = (product?: SrpProductData) => {
    if (product) {
      clearErrors(formNamePrefix);
    } else {
      setError(getFieldName("product"), { type: "required" });
    }
  };

  useEffect(() => {
    let productToUpdate: SrpProductData = selectedProduct as SrpProductData;
    if (
      selectedProduct &&
      isProductPMA(fakeDescriptorFromProduct(selectedProduct)) &&
      selectedProduct.successorProduct
    ) {
      productToUpdate = selectedProduct.successorProduct as SrpProductData;
      setUpdatedWithSuccessor(true);
      setSuccessorProduct(productToUpdate);
      setValue(getFieldName("productId"), productToUpdate!.designation);
    } else if (!successorProduct || successorProduct.designation !== selectedProduct?.designation) {
      setUpdatedWithSuccessor(false);
      setSuccessorProduct(null);
    }

    if (fromSuccessorClick.current) {
      fromSuccessorClick.current = false;
    } else {
      setValue(getFieldName("previousProductId"), "");
    }

    setValue(getFieldName("product"), productToUpdate);

    if (productError) {
      refreshProductError(productToUpdate);
    }
  }, [selectedProduct?.code]);

  const isMinTablet = useMediaQuery(MEDIA_QUERY_MIN_TABLET);
  const isPhone = !isMinTablet;

  let validationResult: ReactNode[] = [];
  if (hasProductConfigured(item) && validateRow) {
    validationResult = validateRow(item, items);
  }

  const quantityVisible = () => {
    if (!product) {
      return true;
    }
    return product ? product.quantityVisible : false;
  };

  return (
    <RowContainer data-test-id={"quick-entry-row"} {...rest}>
      {!isPhone && (
        <RowLeftSide>
          <Box sx={{ px: 3, mt: 3 }}>{index + 1}</Box>
        </RowLeftSide>
      )}
      <RowRightSide $isPhone={isPhone}>
        <Card disableHover={true} className={isMinTablet ? "py-3" : "py-3 px-2"}>
          <ColContainer $isPhone={isPhone} $isMinTablet={isMinTablet} $columns={rightSideColumns}>
            <AutosuggestProductCol $isPhone={isPhone}>
              <AutosuggestProductInput
                {...inputProps}
                label={t("bulkUpload.table.header.productId")}
                inputValue={productId ?? ""}
                onInputChange={(event, value, reason) => {
                  if (reason !== "reset") {
                    setValue(getFieldName("productId"), value);
                  }
                }}
                onChange={(event, value) => {
                  if (!isSameProduct(value ?? "", selectedProduct)) {
                    setValue(getFieldName("productId"), value ?? "");
                  }
                  refreshProductError(selectedProduct);
                }}
                onBlur={() => {
                  refreshProductError(selectedProduct);
                }}
                products={products}
                disableNewVerLink={true}
                className={isPhone ? "w-100" : undefined}
                helperText={" "}
              />
            </AutosuggestProductCol>
            {["wishlist", "b2c-cart"].includes(variant) && (
              <div>
                <Controller
                  render={({ field: { value, onChange, onBlur } }) => (
                    <>
                      <CustomNumberFormat
                        hidden={!quantityVisible()}
                        data-test-id={"quick-entry-item-quantity"}
                        label={t("cartPage.table.header.quantity.title")}
                        handleState={(state) => onChange(state?.floatValue ?? null)}
                        error={!!qtyError}
                        value={value as number | null}
                        onBlur={onBlur}
                      />
                      {!quantityVisible() && <Parameter label={t("input.quantity")} value={item.quantity} />}
                    </>
                  )}
                  name={getFieldName("quantity")}
                  control={control}
                  defaultValue={item.quantity}
                  // @ts-ignore
                  rules={VALIDATE_QTY}
                />
              </div>
            )}

            <DeleteButtonCell $isPhone={isPhone}>
              <ActionIconButton
                data-test-id={"quick-entry-item-btn-remove"}
                className={"ml-3"}
                icon={<DeleteIcon />}
                onIconClick={onRemove}
              />
            </DeleteButtonCell>
          </ColContainer>

          <RowInfoMessages>
            {productError && <Alert severity={"error"}>{t("bulkUpload.error.product.id.invalid")}</Alert>}
            {qtyError && (
              <Alert className="quantity" severity={"error"}>
                {t("bulkUpload.error.product.quantity.invalid")}
              </Alert>
            )}
            {validationResult.map((node) => node)}
            {product && product.isPMA && <InfoProductMessagePMA />}
            {product && NewVersionMessage.isVisible(product) && (
              <NewVersionMessage
                baseProduct={product}
                disableLink={true}
                onClick={() => {
                  fromSuccessorClick.current = true;
                  setValue(getFieldName("previousProductId"), productId);
                  setValue(getFieldName("productId"), product.successorProduct!.designation);
                }}
              />
            )}
            {isUpdatedWithSuccessor && <Alert severity={"info"}>{t("quickEntry.updatedWithSuccessor")}</Alert>}
            {previousProductId && (
              <UndoButton
                onClick={() => {
                  setValue(getFieldName("previousProductId"), "");
                  setValue(getFieldName("productId"), previousProductId);
                }}
              />
            )}
          </RowInfoMessages>
        </Card>
      </RowRightSide>
    </RowContainer>
  );
};

const VALIDATE_QTY = {
  validate: (value: number) => !!value && value >= 1,
};

const RowContainer = styled("div")({
  display: "flex",
  width: "100%",
});

const RowLeftSide = styled("div")({
  width: "11%",
});

const RowRightSide = styled("div")<{ $isPhone: boolean }>(({ $isPhone }) => ({
  width: $isPhone ? "100%" : "89%",
}));

const ColContainer = styled("div")<{ $isPhone: boolean; $isMinTablet: boolean; $columns?: readonly string[] }>(({
  $isPhone,
  theme,
  $isMinTablet,
  $columns,
}) => {
  if (!$isMinTablet) {
    return {
      display: "grid",
      gridTemplateColumns: "226px 1fr",
      "& > *": {
        gridColumn: "1 / 2",
      },
      padding: `0 ${theme.spacing(2)}`,
      gap: theme.spacing(2),
    };
  }

  const cols = $columns ? $columns.map((c) => c) : ["61.2%", "38.8%"];

  return {
    display: "flex",
    "& > *": {
      padding: $isPhone ? 0 : `0 ${theme.spacing(2)}`,
    },
    "& > *:nth-child(1)": { width: cols[0] },
    "& > *:nth-child(2)": { width: cols[1] },
    "& > *:nth-child(3)": { width: cols[2] },
  };
});

const AutosuggestProductCol = styled("div")<{ $isPhone: boolean }>(({ $isPhone }) => ({
  flex: $isPhone ? 1 : "auto",
  width: "100%",
}));

const DeleteButtonCell = styled("div")<{ $isPhone: boolean }>(({ $isPhone }) => ({
  display: "flex",
  justifyContent: "flex-end",
  ...($isPhone ? { gridRow: "1 / 2", gridColumn: "2 / 3" } : { flex: 1 }),
}));

const RowInfoMessages = styled("div")(({ theme }) => ({
  padding: `0 ${theme.spacing(2)}`,
  marginTop: theme.spacing(2),
  [theme.breakpoints.up("tablet")]: {
    marginTop: theme.spacing(4),
  },
  "& .scha__message": {
    display: "inline-flex !important",
    "& > .text": {
      margin: "0 !important",
    },
  },
}));
